import {
    DOMConversionMap,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    LexicalUpdateJSON,
    NodeKey,
    Spread,
} from 'lexical';
import {
    TableCellHeaderStates,
    TableCellNode,
    type SerializedTableCellNode,
} from '@lexical/table';

export type RawHtmlTableCellStyle =
{
    border?: string;
    padding?: string;
    width?: string;
    verticalAlign?: string;
    textAlign?: string;
};

export type SerializedRawHtmlTableCellNode = Spread<{
    rawHtmlTableCellStyle?: RawHtmlTableCellStyle;
}, SerializedTableCellNode>;

function applyCellStyle(element: HTMLElement, style?: RawHtmlTableCellStyle)
{
    // Czyścimy tylko właściwości, którymi zarządza ten node.
    // Dzięki temu usunięcie stylu z node'a usuwa go też z DOM.
    element.style.removeProperty('border');
    element.style.removeProperty('padding');
    element.style.removeProperty('width');
    element.style.removeProperty('vertical-align');
    element.style.removeProperty('text-align');

    if (!style) { return; }

    if (style.border) {
        element.style.border = style.border;
    }

    if (style.padding) {
        element.style.padding = style.padding;
    }

    if (style.width) {
        element.style.width = style.width;
    }

    if (style.verticalAlign) {
        element.style.verticalAlign = style.verticalAlign;
    }

    if (style.textAlign) {
        element.style.textAlign = style.textAlign;
    }
}

function readCellStyle(element: HTMLElement): RawHtmlTableCellStyle
{
    const style: RawHtmlTableCellStyle = {};

    if (element.style.border) { style.border = element.style.border; }
    if (element.style.padding) { style.padding = element.style.padding; }
    // if (element.style.width) { style.width = element.style.width; }
    if (element.style.verticalAlign) { style.verticalAlign = element.style.verticalAlign; }
    if (element.style.textAlign) { style.textAlign = element.style.textAlign; }

    return style;
}

function copyTableCellInternalState(source: TableCellNode, target: TableCellNode): void
{
    (target as any).__rowSpan = (source as any).__rowSpan;
    (target as any).__backgroundColor = (source as any).__backgroundColor;
    (target as any).__verticalAlign = (source as any).__verticalAlign;
}

export class RawHtmlTableCellNode extends TableCellNode
{
    __rawHtmlTableCellStyle?: RawHtmlTableCellStyle;

    static getType(): string
    {
        return 'raw-html-table-cell';
    }

    static clone(node: RawHtmlTableCellNode): RawHtmlTableCellNode
    {
        const cloned = new RawHtmlTableCellNode
        (
            node.getHeaderStyles(),
            node.getColSpan(),
            // eslint-disable-next-line no-undefined
            undefined,
            node.__rawHtmlTableCellStyle,
            node.__key
        );

        copyTableCellInternalState(node, cloned);
        return cloned;
    }

    static importJSON(serializedNode: SerializedRawHtmlTableCellNode): RawHtmlTableCellNode
    {
        return new RawHtmlTableCellNode().updateFromJSON(serializedNode);
    }

    static importDOM(): DOMConversionMap | null
    {
        const base = TableCellNode.importDOM?.() ?? {};

        const convert = (element: HTMLElement) =>
        {
            const tag = element.tagName.toLowerCase();
            const baseConverter = base[tag]?.(element);
            const output = baseConverter?.conversion(element);

            const headerState =
                tag === 'th'
                    ? TableCellHeaderStates.ROW
                    : TableCellHeaderStates.NO_STATUS;

            const colSpan = element instanceof HTMLTableCellElement
                ? element.colSpan
                : 1;

            const rawNode = new RawHtmlTableCellNode
            (
                headerState,
                colSpan,
                // eslint-disable-next-line no-undefined
                undefined,
                readCellStyle(element)
            );

            return { ...output, node: rawNode };
        };

        return {
            ...base,
            td: () => ({ priority: 4, conversion: convert }),
            th: () => ({ priority: 4, conversion: convert }),
        };
    }

    constructor
    (
        headerState: number = TableCellHeaderStates.NO_STATUS,
        colSpan = 1,
        width?: number,
        style?: RawHtmlTableCellStyle,
        key?: NodeKey
    )
    {
        super(headerState, colSpan, width, key);
        this.__rawHtmlTableCellStyle = style;
    }

    createDOM(config: EditorConfig): HTMLTableCellElement
    {
        const element = super.createDOM(config);
        applyCellStyle(element, this.__rawHtmlTableCellStyle);
        return element;
    }

    updateDOM(prevNode: this): boolean
    {
        const shouldRecreate = super.updateDOM(prevNode);

        // TableCellNode.updateDOM w tej wersji Lexicala nie dostaje DOM elementu,
        // więc jeśli zmienił się nasz styl, prosimy Lexicala o rekreację DOM.
        return shouldRecreate ||
            prevNode.__rawHtmlTableCellStyle !== this.__rawHtmlTableCellStyle;
    }

    exportDOM(editor: LexicalEditor): DOMExportOutput
    {
        const output = super.exportDOM(editor);

        if (output.element instanceof HTMLElement) {
            applyCellStyle(output.element, this.__rawHtmlTableCellStyle);

            // Standardowy TableCellNode potrafi wypuścić width.
            // W raw-html nie chcemy go eksportować, jeśli użytkownik nie ustawił go jawnie.
            if (!this.__rawHtmlTableCellStyle?.width) {
                output.element.style.removeProperty('width');
            }
        }

        return output;
    }

    exportJSON(): SerializedRawHtmlTableCellNode
    {
        const json =
        {
            ...super.exportJSON(),
            type: 'raw-html-table-cell',
            rawHtmlTableCellStyle: this.__rawHtmlTableCellStyle,
        } as SerializedRawHtmlTableCellNode & { width?: number };

        if (!this.__rawHtmlTableCellStyle?.width) { delete json.width; }
        return json;
    }

    updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedRawHtmlTableCellNode>): this
    {
        return super
            .updateFromJSON(serializedNode)
            .setRawHtmlTableCellStyle(serializedNode.rawHtmlTableCellStyle ?? null);
    }

    setRawHtmlTableCellStyle(style: RawHtmlTableCellStyle | null): this
    {
        const self = this.getWritable();
        self.__rawHtmlTableCellStyle = style ?? void 0;
        return self;
    }

    getRawHtmlTableCellStyle(): RawHtmlTableCellStyle | undefined
    {
        return this.getLatest().__rawHtmlTableCellStyle;
    }
}

export function $createRawHtmlTableCellNodeFrom(node: TableCellNode): RawHtmlTableCellNode
{
    const rawNode = new RawHtmlTableCellNode
    (
        node.getHeaderStyles(),
        node.getColSpan(),
        // eslint-disable-next-line no-undefined
        undefined
    );

    copyTableCellInternalState(node, rawNode);
    return rawNode;
}

export function $isRawHtmlTableCellNode(node: LexicalNode | null | undefined): node is RawHtmlTableCellNode
{
    return node instanceof RawHtmlTableCellNode;
}